import React, { type ReactNode, useCallback, useRef, useMemo } from 'react';

import { useFocusFieldEvent } from '../../../contexts/events-context';
import { useFieldPropsV2 } from '../../../contexts/field-props-context';
import { findChildOfType } from '../../../services/find-child';
import { Description } from '../../field-description/field-description-headless';
import { Error as FieldError } from '../../field-error/field-error-headless';
import { Label } from '../../field-label/field-label-headless';
import { type FileUploadProps as FileUploadInternalProps } from '../file-upload-props';
import {
  useFileUploadController,
  type FileUploadFile,
} from '../use-file-upload-controller';

import { DropZone } from './drop-zone';
import {
  FileUploadContext,
  useFileUploadContext,
  type FileUploadContextValue,
} from './file-upload-context';
import { Files } from './files';

const fileKey = (f: FileUploadFile) => f.file.name;

export type FileUploadProps = {
  children: ReactNode;
};

export const FileUpload = ({ children }: FileUploadProps) => {
  const {
    id,
    value,
    onChange,
    uploadFileFormats,
    valueSliderLimit,
    target,
    explanationText,
    onBlur,
    FieldLayout,
  } = useFieldPropsV2<FileUploadInternalProps>();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const fileRemoveButtonRefs = useRef<{
    [key: string]: React.RefObject<HTMLButtonElement>;
  }>({});

  const handleFilesCleared = useCallback(() => {
    buttonRef.current?.focus();
  }, []);

  const {
    files,
    _error,
    hover,
    getExplanationText,
    handleBlur,
    handleChange,
    handleDelete: _handleDelete,
    accept,
  } = useFileUploadController({
    valueSliderLimit,
    value,
    explanationText,
    target,
    onChange,
    uploadFileFormats,
    onBlur,
    onFilesCleared: handleFilesCleared,
  });

  const handleDelete = useCallback(
    (index: number) => {
      const fileToDelete = files[index];
      const currentKey = fileKey(fileToDelete);

      if (files.length > 1) {
        const firstFocusableElementRef =
          files
            .filter((f) => fileKey(f) !== currentKey)
            .map((f) => fileRemoveButtonRefs.current[fileKey(f)])
            .find(Boolean) ?? buttonRef;

        firstFocusableElementRef.current?.focus();
      }

      _handleDelete(index);
    },
    [_handleDelete, files],
  );

  const multiple = valueSliderLimit > 1;
  const limitReached = files.length >= valueSliderLimit;

  const contextValue: FileUploadContextValue = useMemo(
    () => ({
      handleFileChange: handleChange,
      handleBlur,
      handleDelete,
      files,
      accept,
      limitReached,
      multiple,
      getExplanationText,
      hover,
      _error,
      buttonRef,
      fileRemoveButtonRefs,
    }),
    [
      handleChange,
      handleBlur,
      handleDelete,
      files,
      accept,
      limitReached,
      multiple,
      getExplanationText,
      hover,
      _error,
    ],
  );

  const labelElement = findChildOfType(children, FileUpload.Label);
  const dropZoneElement = findChildOfType(children, FileUpload.DropZone);
  const explanationElement = findChildOfType(children, FileUpload.Explanation);
  const filesElement = findChildOfType(children, FileUpload.Files);
  const descriptionElement = findChildOfType(children, FileUpload.Description);
  const errorElement = findChildOfType(children, FileUpload.Error);

  useFocusFieldEvent(() => {
    buttonRef.current?.focus();
  }, id);

  return (
    <FileUploadContext.Provider value={contextValue}>
      <FieldLayout
        fieldId={id}
        renderLabel={() => labelElement}
        renderInput={() => dropZoneElement}
        renderDescription={() => (
          <>
            {errorElement}
            {descriptionElement}
            {explanationElement}
            {filesElement}
          </>
        )}
      />
    </FileUploadContext.Provider>
  );
};

export type ExplanationProps = {
  className?: string;
};

export const Explanation = ({ className }: ExplanationProps) => {
  const { getExplanationText } = useFileUploadContext();
  const displayText = getExplanationText();

  if (!displayText) {
    return null;
  }

  return <p className={className}>{displayText}</p>;
};

FileUpload.Label = Label;
FileUpload.DropZone = DropZone;
FileUpload.Explanation = Explanation;
FileUpload.Files = Files;
FileUpload.Description = Description;
FileUpload.Error = FieldError;
